import React, { useState, useEffect } from 'react';
import { formatPrice } from '../../utils/formatHelper';
import { apiRequest } from '../../utils/apiHelper';

import placeholderImage from '../../assets/images/placeholder.jpg';
import { showSuccess, showError, showWarning } from '../../utils/toastHelper';

function SubscriptionDetailsContent({ subscription, onClose }) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [paymentCheckResult, setPaymentCheckResult] = useState(null);
  const [showPaymentCheckResult, setShowPaymentCheckResult] = useState(false);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (subscription) {
      setPaymentCheckResult(null);
      setShowPaymentCheckResult(false);
      setIsCheckingPayment(false);
    }
  }, [subscription]);

  const handlePaymentStatusCheck = async (checkType = 'subscription') => {
    setIsCheckingPayment(true);
    setPaymentCheckResult(null);
    setShowPaymentCheckResult(false);

    try {
      let queryParams = '';
      if (checkType === 'subscription') {
        queryParams = `?subscriptionId=${subscription.id}`;
      } else if (checkType === 'user') {
        queryParams = `?userId=${subscription.customer.id}`;
      }

      const response = await apiRequest(`/subscriptions/payment-status${queryParams}`);

      if (response.success) {
        setPaymentCheckResult(response);
        setShowPaymentCheckResult(true);

        if (response.paymentCheck?.needsAction) {
          showWarning(response.paymentCheck.message);
        } else {
          showSuccess('Ödeme durumu kontrol edildi');
        }
      } else {
        showError(response.message || 'Ödeme durumu kontrol edilemedi');
      }
    } catch (error) {
      console.error('Payment status check error:', error);
      showError(error.message || 'Ödeme durumu kontrol edilirken bir hata oluştu');
    } finally {
      setIsCheckingPayment(false);
    }
  };

  if (!subscription) return null;

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'active':
        return 'success';
      case 'waiting':
      case 'WAITING':
        return 'warning';
      case 'cancelled':
        return 'danger';
      case 'pending':
        return 'info';
      case 'failed':
        return 'danger';
      default:
        return 'secondary';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'active':
        return 'Aktif';
      case 'waiting':
      case 'WAITING':
        return 'Duraklatılmış';
      case 'cancelled':
        return 'İptal Edilmiş';
      case 'pending':
        return 'Beklemede';
      case 'failed':
        return 'Hata Durumu';
      default:
        return status;
    }
  };

  return (
    <div className="subscription-details-content">
      {/* Customer Info */}
      <div className="card mb-4">
        <div className="card-header bg-light">
          <h6 className="mb-0">Müşteri Bilgileri</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <p className="mb-1 text-muted">Ad Soyad</p>
              <p className="fw-bold">{subscription.customer.name}</p>
            </div>
            <div className="col-md-6">
              <p className="mb-1 text-muted">E-posta</p>
              <p className="fw-bold">{subscription.customer.email}</p>
            </div>
            <div className="col-12">
              <p className="mb-1 text-muted">Üyelik Tarihi</p>
              <p className="fw-bold">
                {new Date(subscription.created_at).toLocaleDateString('tr-TR')}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Subscription Info */}
      <div className="card mb-4">
        <div className="card-header bg-light">
          <h6 className="mb-0">Abonelik Bilgileri</h6>
        </div>
        <div className="card-body">
          <div className="row">
            <div className="col-md-6">
              <p className="mb-1 text-muted">Plan</p>
              <p className="fw-bold">{subscription.plan.name}</p>
            </div>
            <div className="col-md-6">
              <p className="mb-1 text-muted">Durum</p>
              <div className={`status-pill ${subscription.status}`}>
                <i className={`fas fa-${subscription.status === 'active' ? 'check-circle' :
                  (subscription.status === 'waiting' || subscription.status === 'WAITING') ? 'pause-circle' :
                    subscription.status === 'cancelled' ? 'times-circle' :
                      subscription.status === 'failed' ? 'exclamation-triangle' :
                        'clock'} me-2`}></i>
                {getStatusText(subscription.status)}
              </div>
            </div>
            <div className="col-md-6">
              <p className="mb-1 text-muted">Başlangıç Tarihi</p>
              <p className="fw-bold">
                {new Date(subscription.start_date).toLocaleDateString('tr-TR')}
              </p>
            </div>
            <div className="col-md-6">
              <p className="mb-1 text-muted">Sonraki Ödeme</p>
              <p className="fw-bold">
                {subscription.next_payment_date ?
                  new Date(subscription.next_payment_date).toLocaleDateString('tr-TR') :
                  '-'
                }
              </p>
            </div>
            <div className="col-md-6">
              <p className="mb-1 text-muted">Aylık Tutar</p>
              <p className="fw-bold">
                {formatPrice(subscription.amount)}
              </p>
            </div>
            <div className="col-md-6">
              <p className="mb-1 text-muted">Teslimat Sıklığı</p>
              <p className="fw-bold">
                {subscription.plan.interval === 'WEEKLY'
                  ? `${subscription.plan.intervalCount} Haftalık`
                  : subscription.plan.interval === 'MONTHLY'
                    ? `${subscription.plan.intervalCount} Aylık`
                    : subscription.plan.interval === 'BIWEEKLY'
                      ? '2 Haftalık'
                      : subscription.plan.interval}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Products */}
      <div className="card mb-4">
        <div className="card-header bg-light">
          <h6 className="mb-0">Ürünler</h6>
        </div>
        <div className="table-responsive">
          <table className="table table-hover align-middle mb-0">
            <thead className="bg-light">
              <tr>
                <th>Ürün</th>
                <th>Miktar</th>
                <th>Birim Fiyat</th>
                <th>Toplam</th>
              </tr>
            </thead>
            <tbody>
              {subscription.products.map((product) => (
                <tr key={product.id}>
                  <td>
                    <div className="d-flex align-items-center">
                      <img
                        src={product.mainImage || placeholderImage}
                        alt={product.name}
                        className="rounded"
                        style={{ width: '40px', height: '40px', objectFit: 'cover' }}
                        onError={(e) => { e.target.onerror = null; e.target.src = placeholderImage; }}
                      />
                      <div className="ms-3">
                        <h6 className="mb-0">{product.name}</h6>
                      </div>
                    </div>
                  </td>
                  <td>{product.quantity}</td>
                  <td>{formatPrice(product.price)}</td>
                  <td>{formatPrice(product.price * product.quantity)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Payment Status Check */}
      <div className="card">
        <div className="card-header bg-light">
          <h6 className="mb-0">
            <i className="fas fa-credit-card me-2"></i>
            Iyzico Ödeme Durumu
          </h6>
        </div>
        <div className="card-body">
          <div className="row g-3">
            <div className="col-md-6">
              <button
                className="btn btn-info w-100"
                onClick={() => handlePaymentStatusCheck('subscription')}
                disabled={isCheckingPayment}
                title="Müşteriden ödeme çekmez, sadece Iyzico'daki güncel durumu sorgular."
              >
                {isCheckingPayment ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                    Sorgulanıyor...
                  </>
                ) : (
                  <>
                    <i className="fas fa-search me-2"></i>
                    Iyzico'dan Durum Sorgula
                  </>
                )}
              </button>
              <small className="text-muted d-block mt-2">
                <i className="fas fa-info-circle me-1"></i>
                Ödeme çekmez, sadece Iyzico'daki güncel durumu getirir.
              </small>
            </div>
          </div>

          {/* Payment check result */}
          {showPaymentCheckResult && paymentCheckResult && (
            <div className="alert alert-info mt-3 payment-check-result">
              <h6 className="alert-heading">
                <i className="fas fa-info-circle me-2"></i>
                Kontrol Sonucu
              </h6>
              <p className="mb-2">{paymentCheckResult.paymentCheck?.message}</p>
              {paymentCheckResult.paymentCheck?.recommendation && (
                <p className="mb-0 small">
                  <strong>Öneri:</strong> {paymentCheckResult.paymentCheck.recommendation}
                </p>
              )}

              {/* Show Iyzico details if available */}
              {paymentCheckResult.iyzicoDetails && (
                <div className="mt-3 pt-3 border-top">
                  <div className="row">
                    <div className="col-sm-6">
                      <small className="text-muted d-block">
                        <strong>Iyzico Durumu:</strong> {paymentCheckResult.iyzicoDetails.subscriptionStatus}
                      </small>
                    </div>
                    <div className="col-sm-6">
                      <small className="text-muted d-block">
                        <strong>Başarılı Ödemeler:</strong> {paymentCheckResult.iyzicoDetails.paidOrders}
                      </small>
                    </div>
                    <div className="col-sm-6">
                      <small className="text-muted d-block">
                        <strong>Başarısız Ödemeler:</strong> {paymentCheckResult.iyzicoDetails.failedOrders}
                      </small>
                    </div>
                    {paymentCheckResult.iyzicoDetails.lastPaymentInfo && (
                      <div className="col-sm-6">
                        <small className="text-muted d-block">
                          <strong>Son Ödeme:</strong> {new Date(paymentCheckResult.iyzicoDetails.lastPaymentInfo.date).toLocaleDateString('tr-TR')}
                        </small>
                      </div>
                    )}
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>


      <style>
        {`
          .subscription-details-content {
            margin: -1rem;
            padding: 1rem;
          }

          .card {
            border: none;
            box-shadow: 0 0.125rem 0.25rem rgba(0, 0, 0, 0.075);
          }
          
          .card-header {
            background-color: #f8f9fa;
            border-bottom: none;
            padding: 1rem;
          }

          .card-body {
            padding: 1.25rem;
          }

          .btn-info {
            background-color: #17a2b8;
            border-color: #17a2b8;
            transition: all 0.3s ease;
          }

          .btn-info:hover:not(:disabled) {
            background-color: #138496;
            border-color: #117a8b;
            transform: translateY(-1px);
          }

          .btn:disabled {
            opacity: 0.7;
            cursor: not-allowed;
            transform: none !important;
          }

          .payment-check-result {
            animation: fadeInUp 0.4s ease-out;
          }

          @keyframes fadeInUp {
            from { 
              opacity: 0;
              transform: translateY(10px);
            }
            to { 
              opacity: 1;
              transform: translateY(0);
            }
          }

          .spinner-border-sm {
            width: 1rem;
            height: 1rem;
          }

          .alert-heading {
            margin-bottom: 0.75rem;
            font-weight: 600;
          }
          
          .table > :not(caption) > * > * {
            padding: 1rem;
          }

          .status-pill {
            display: inline-flex;
            align-items: center;
            padding: 6px 12px;
            border-radius: 6px;
            font-size: 0.85rem;
            font-weight: 500;
          }

          .status-pill.active {
            background-color: #00b8941a;
            color: #00b894;
          }

          .status-pill.waiting, .status-pill.WAITING {
            background-color: #ffeaa71a;
            color: #fdcb6e;
          }

          .status-pill.cancelled {
            background-color: #ff76751a;
            color: #ff7675;
          }

          .status-pill.pending {
            background-color: #74b9ff1a;
            color: #0984e3;
          }

          .status-pill i {
            font-size: 14px;
          }

          .table img {
            border: 1px solid #dee2e6;
          }

          @media (max-width: 767px) {
            .subscription-details-content {
              margin: -1rem;
              padding: 1rem;
            }

            .card-header h6 {
              font-size: 0.95rem;
            }
            
            .table > :not(caption) > * > * {
              padding: 0.5rem;
            }

            .status-pill {
              font-size: 0.8rem;
              padding: 4px 8px;
            }
          }
        `}
      </style>
    </div>
  );
}

export default SubscriptionDetailsContent; 